
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Crown, AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * Componente para exibir funcionalidades restritas por plano
 * 
 * O que faz:
 * - Mostra uma interface consistente para recursos bloqueados
 * - Indica o motivo da restrição (plano PMK, falta de permissão, etc.)
 * - Fornece orientações sobre como liberar o recurso
 * 
 * Onde é usado:
 * - Em qualquer componente que precisa restringir acesso baseado no plano
 * - Chat IA, Relatórios, Configurações avançadas, etc.
 * 
 * Como se conecta:
 * - Recebe informações do plano e tipo de restrição
 * - Mostra mensagens específicas para cada contexto
 */

interface RestrictedFeatureProps {
  planName: string;
  featureName?: string;
  featureDescription?: string; // NOVO: Descrição do recurso para tooltip
  showUpgrade?: boolean;
  isAdmin?: boolean;
  hasOverride?: boolean;
  onRequestAccess?: () => void;
  onUpgradeClick?: () => void; // NOVO: Callback para upgrade
  variant?: 'card' | 'inline' | 'modal';
  hideForPMK?: boolean; // NOVO: Se true, não renderiza nada para plano PMK
  className?: string;
}

export const RestrictedFeature = ({
  planName,
  featureName = 'este recurso',
  featureDescription, // NOVO: Descrição para tooltip
  showUpgrade = false,
  isAdmin = false,
  hasOverride = false,
  onRequestAccess,
  onUpgradeClick, // NOVO: Callback para upgrade
  variant = 'card',
  hideForPMK = false, // NOVO: Esconder para plano PMK
  className = ''
}: RestrictedFeatureProps) => {
  // NOVO: Se hideForPMK é true e o plano é PMK, não renderiza nada
  const isPMK = planName?.toUpperCase() === 'PMK';
  if (hideForPMK && isPMK) {
    return null;
  }
  // Determinar ícone e cor baseado no tipo de restrição
  const getRestrictionInfo = () => {
    if (planName === 'PMK') {
      return {
        icon: AlertTriangle,
        color: 'text-amber-600',
        bgColor: 'bg-amber-50 border-amber-200',
        badgeVariant: 'secondary' as const,
        title: `${featureName} Restrito`,
        message: hasOverride
          ? 'Recurso liberado pelo administrador'
          : 'Este recurso está restrito no plano PMK. Um administrador pode liberar o acesso.',
        actionText: 'Solicitar Liberação'
      };
    }

    return {
      icon: Lock,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50 border-gray-200',
      badgeVariant: 'outline' as const,
      title: `${featureName} Indisponível`,
      message: `${featureName} não está disponível no seu plano atual.`,
      actionText: showUpgrade ? 'Fazer Upgrade' : 'Saiba Mais'
    };
  };

  const restrictionInfo = getRestrictionInfo();
  const Icon = restrictionInfo.icon;

  // Variante inline (para uso em botões desabilitados, etc.)
  // MODIFICADO: Adiciona tooltip com descrição e sugestão de upgrade para planos não-PMK
  if (variant === 'inline') {
    // Conteúdo do tooltip com descrição e sugestão de upgrade
    const tooltipContent = (
      <div className="max-w-xs space-y-2 p-1">
        {featureDescription && (
          <p className="text-sm text-gray-100">{featureDescription}</p>
        )}
        {showUpgrade && (
          <div className="flex items-center gap-1.5 text-sm text-amber-300 pt-1 border-t border-gray-600">
            <ArrowUpCircle className="w-4 h-4" />
            <span>Faça upgrade para desbloquear</span>
          </div>
        )}
      </div>
    );

    const inlineElement = (
      <div className={`flex items-center gap-2 text-sm text-gray-500 cursor-help ${className}`}>
        <Icon className="w-4 h-4" />
        <span>Restrito no plano {planName}</span>
        {hasOverride && (
          <Badge variant="default" className="text-xs">
            <Crown className="w-3 h-3 mr-1" />
            Liberado
          </Badge>
        )}
      </div>
    );

    // Se tem descrição ou showUpgrade, envolve com tooltip
    if (featureDescription || showUpgrade) {
      return (
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              {inlineElement}
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              className="bg-gray-800 text-white border-gray-700"
            >
              {tooltipContent}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return inlineElement;
  }

  // Variante modal (para uso em diálogos)
  if (variant === 'modal') {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className={`w-16 h-16 ${restrictionInfo.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
          <Icon className={`w-8 h-8 ${restrictionInfo.color}`} />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {restrictionInfo.title}
        </h3>
        <p className="text-gray-600 mb-4 max-w-sm mx-auto">
          {restrictionInfo.message}
        </p>
        <div className="flex items-center justify-center gap-3">
          <Badge variant={restrictionInfo.badgeVariant}>
            Plano {planName}
          </Badge>
          {hasOverride && (
            <Badge variant="default">
              <Crown className="w-3 h-3 mr-1" />
              Admin Override
            </Badge>
          )}
        </div>
        {(onRequestAccess || showUpgrade) && (
          <Button
            onClick={onRequestAccess}
            variant="outline"
            className="mt-4"
          >
            {restrictionInfo.actionText}
          </Button>
        )}
      </div>
    );
  }

  // Variante card (padrão)
  return (
    <Card className={`${restrictionInfo.bgColor} ${className}`}>
      <CardHeader className="text-center pb-3">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Icon className={`w-6 h-6 ${restrictionInfo.color}`} />
          <CardTitle className="text-lg text-gray-900">
            {restrictionInfo.title}
          </CardTitle>
        </div>
        <div className="flex items-center justify-center gap-2">
          <Badge variant={restrictionInfo.badgeVariant}>
            Plano {planName}
          </Badge>
          {hasOverride && (
            <Badge variant="default">
              <Crown className="w-3 h-3 mr-1" />
              Admin Override
            </Badge>
          )}
          {isAdmin && (
            <Badge variant="default">
              <Crown className="w-3 h-3 mr-1" />
              Admin
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="text-center">
        <p className="text-gray-700 mb-4">
          {restrictionInfo.message}
        </p>
        {(onRequestAccess || showUpgrade) && (
          <Button
            onClick={onRequestAccess}
            variant="outline"
            size="sm"
          >
            {restrictionInfo.actionText}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
